import { memo, useCallback, useMemo, useState } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { useClasses } from "@api/class.api";
import { ClassWithChildren } from "@helpers/class";
import { ClassCombobox } from "../../transactions/TransactionSplitV2/ClassCombobox";
import { getClassesAriaLabel } from "../../transactions/TransactionSplitV2/getClassesAriaLabel";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";

export const ClassesNewValueCell: React.FC<{
    transactionId: number;
    classIds: string[];
}> = memo(({ transactionId, classIds }) => {
    const { setNewValue } = useSimilarTransactionsNewValue();
    const { classesMap, sortedClassesIndexMap } = useClasses();
    const [isOpen, setIsOpen] = useState(false);

    const resolvedClasses = useMemo(() => {
        if (!classIds.length) {
            return [];
        }
        return classIds
            .map((id) => classesMap.get(id))
            .filter((c): c is ClassWithChildren => Boolean(c))
            .toSorted((a, b) => {
                const aIdx = sortedClassesIndexMap.get(a.id) ?? Infinity;
                const bIdx = sortedClassesIndexMap.get(b.id) ?? Infinity;
                return aIdx - bIdx;
            });
    }, [classIds, classesMap, sortedClassesIndexMap]);

    const handleClassesChange = useCallback(
        (newClassIds: string[]) => {
            setNewValue(transactionId, newClassIds);
        },
        [transactionId, setNewValue],
    );

    const trigger = useMemo(
        () => (
            <Picker.CellContent
                value={
                    resolvedClasses.length > 0 ? (
                        <ClassAvatarGroup classes={resolvedClasses} max={3} />
                    ) : undefined
                }
                aria-label={getClassesAriaLabel(resolvedClasses)}
            />
        ),
        [resolvedClasses],
    );

    return (
        <ViewTableCell captureClicks>
            <ClassCombobox
                selectedClassIds={classIds}
                onChange={handleClassesChange}
                hideArrow
                open={isOpen}
                onOpenChange={setIsOpen}
                trigger={trigger}
            />
        </ViewTableCell>
    );
});
ClassesNewValueCell.displayName = "ClassesNewValueCell";
